import { useTranslation } from "react-i18next";
import { VIDEO_SRC_BY_LANG } from "@/components/sections/BrandVideo";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Calendar, ArrowRight, CheckCircle2, Scale, ShieldCheck, AlertTriangle, TrendingDown, Users, Clock } from "lucide-react";
import { ClientLogos } from "@/components/ClientLogos";
import { ScheduleDemo } from "@/components/sections";
import { PartnerForChange } from "@/components/sections/PartnerForChange";
import { PageResources } from "@/components/sections/PageResources";
import { PageContainer } from "@/components/layouts/MainLayout";
import { T, THtml } from "@/components/editable/T";
import { EditableImage } from "@/components/editable/EditableImage";
import { getLocalizedRoutePath } from "@/lib/routeUtils";
import compensationHero from "@/assets/images/compensation-hero-photo.webp";
import salarisrondeNl from "@/assets/images/salarisronde.webp";
import salarisrondeEn from "@/assets/images/salarisronde-en.webp";
import salarisrondeDe from "@/assets/images/salarisronde-de.webp";
import loonkloofNl from "@/assets/images/loonkloofanalyse-nl.webp";
import loonkloofEn from "@/assets/images/loonkloofanalyse-en.webp";
import loonkloofDe from "@/assets/images/loonkloofanalyse-de.webp";
import beloningsoverzichtNl from "@/assets/images/beloningsoverzicht-nl.webp";
import beloningsoverzichtEn from "@/assets/images/beloningsoverzicht-en.webp";
import beloningsoverzichtDe from "@/assets/images/beloningsoverzicht-de.webp";

const P = "platform-compensation";

const featureColors = [
"#F3EDFD", "#FDF2ED", "#FFFBEB"];


const PlatformCompensation = () => {
  const { i18n } = useTranslation();

  const lang = i18n.language || 'nl';

  const features = [
  {
    eyebrowKey: "cycle.eyebrow",
    eyebrowDefault: "Compensation Cycle (salarisronde)",
    titleKey: "cycle.title",
    titleDefault: "Een salarisronde die snel en gesmeerd verloopt",
    bodyKey: "cycle.body",
    bodyDefault: "Geen losse Excel-sheets meer: je salarisronde verloopt via één cyclus met budget, merit en een vaste goedkeuringsflow, zodat HR altijd in control blijft en niets tussen wal en schip valt.",
    benefits: [
    { k: "cycle.benefit1", d: "Geen tijdrovende en foutgevoelige Excel-exercitie" },
    { k: "cycle.benefit2", d: "Verdeel merit en budget in één overzicht, per team of afdeling" },
    { k: "cycle.benefit3", d: "Vaste goedkeuringsflow en eigen importeerbare beloningsbeleid" }],

    imageKey: 'row1-image',
    badge: { k: "cycle.badge", d: "Gekoppeld aan performance en HRIS" },
    imgSrc: lang === 'en' ? salarisrondeEn : lang === 'de' ? salarisrondeDe : salarisrondeNl,
    imgAlt: lang === 'en' ? 'Salary round overview with budget, merit and justification' : lang === 'de' ? 'Gehaltsrunde mit Budget, Verdienst und Begründung' : 'Salarisronde met budget, merit en onderbouwing',
    color: featureColors[0],
    cta: { labelKey: "features.row1.cta", labelDefault: "Ontdek de beloningsronde", link: getLocalizedRoutePath("compensationRound", lang) }
  },
  {
    eyebrowKey: "payGap.eyebrow",
    eyebrowDefault: "Loonkloofanalyse",
    titleKey: "payGap.title",
    titleDefault: "Zie het verschil terwijl je het nog kunt herstellen",
    bodyKey: "payGap.body",
    bodyDefault: "Vergelijk doorlopend hoe elke medewerker ervoor staat binnen zijn functiegroep, spoor onverklaarde loonverschillen op (ook tussen man en vrouw) en herstel ze voordat de verplichte rapportage het voor je doet.",
    benefits: [
    { k: "payGap.benefit1", d: "Percentage onverklaard loonverschil direct zichtbaar" },
    { k: "payGap.benefit2", d: "Verklarende criteria: opleiding, werkervaring, beoordelingen" },
    { k: "payGap.benefit3", d: "Altijd klaar voor de verplichte rapportage" }],

    imageKey: 'row3-image',
    imgSrc: lang === 'en' ? loonkloofEn : lang === 'de' ? loonkloofDe : loonkloofNl,
    imgAlt: lang === 'en' ? 'Wage gap analysis per job group, male versus female' : lang === 'de' ? 'Lohnkluftanalyse nach Funktionsgruppe, Mann gegen Frau' : 'Loonkloofanalyse per functiegroep, man tegen vrouw',
    color: featureColors[1],
    cta: { labelKey: "features.row3.cta", labelDefault: "Ontdek beloningstransparantie en equal pay", link: getLocalizedRoutePath("payTransparencyEqualPay", lang) }
  },
  {
    eyebrowKey: "statement.eyebrow",
    eyebrowDefault: "Beloningsoverzicht",
    titleKey: "statement.title",
    titleDefault: "Een transparant, eerlijk en inzichtelijk antwoord op iedere vraag",
    bodyKey: "statement.body",
    bodyDefault: "Onderbouw elk salaris automatisch op basis van functiehuis en niveau, in één overzicht dat je zo kunt delen met een medewerker.",
    benefits: [
    { k: "statement.benefit1", d: "Gefundeerde onderbouwing per medewerker" },
    { k: "statement.benefit2", d: "Directe koppeling aan functiehuis en niveau" },
    { k: "statement.benefit3", d: "Criteria inzichtelijk voor iedereen" }],

    imageKey: 'row2-image',
    imgSrc: lang === 'en' ? beloningsoverzichtEn : lang === 'de' ? beloningsoverzichtDe : beloningsoverzichtNl,
    imgAlt: lang === 'en' ? 'Compensation statement with old and new salary and reasoning' : lang === 'de' ? 'Vergütungsübersicht mit altem und neuem Gehalt und Begründung' : 'Beloningsoverzicht met oud en nieuw salaris en onderbouwing',
    color: featureColors[2],
    demoCta: true
  }];


  return (
    <>
      {/* Hero Section */}
      <section className="pt-[140px] pb-6 px-6 relative">
        <PageContainer className="relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center mb-12">
            <div className="animate-fade-in">
              <p className="text-lead text-muted-foreground mb-4">
                <T page={P} k="hero.eyebrow" d="Learned Compensation" />
              </p>
              <h1 className="mb-6" style={{ color: '#2A2334' }}>
                <T page={P} k="hero.titleLead" d="Een beloningsbeleid" as="span" className="heading-lead" />
                <T page={P} k="hero.titleAccent" d="dat je kunt onderbouwen" as="span" className="heading-accent" />
              </h1>
              <THtml page={P} k="hero.intro" d="Van salarisronde tot loonkloofanalyse: bepaal niet alleen wát iemand verdient, maar ook waaróm. Met eerlijke en transparante criteria die voor iedereen hetzelfde zijn, en die je moeiteloos kunt uitleggen." as="div" className="text-lead text-muted-foreground mb-8 max-w-xl" />
              <div className="flex flex-col sm:flex-row gap-4">
                <Button size="lg" className="text-sm px-6 py-5 rounded-full bg-[#9055EE] hover:bg-[#753DCE]" onClick={() => window.open('https://app.learned.io/register', '_blank')}>
                  <T page={P} k="hero.tryFree" d="Probeer gratis" />
                </Button>
                <Button variant="outline" size="lg" className="text-sm px-6 py-5 rounded-full bg-white hover:bg-gray-50" onClick={() => document.getElementById('schedule-demo')?.scrollIntoView({ behavior: 'smooth' })}>
                  <Calendar className="mr-2 h-4 w-4" style={{ color: '#9055EE' }} />
                  <T page={P} k="hero.planDemo" d="Plan een demo" />
                </Button>
              </div>
            </div>

            <div className="animate-fade-in relative" style={{ animationDelay: '200ms' }}>
              <EditableImage
                page={P}
                section="hero"
                imageKey="hero-image"
                src={compensationHero}
                alt="Twee collega's in gesprek over beloning"
                className="w-full h-[450px] object-cover object-center rounded-2xl"
                loading="eager"
              />
              <div className="absolute -right-8 -top-5 bg-white rounded-full shadow-xl ring-[8px] ring-white/80 px-4 py-2.5 transform rotate-3 hover:rotate-0 transition-transform duration-300 hidden lg:flex items-center gap-2 opacity-0 animate-fade-float-in" style={{ animationDelay: '1100ms', animationFillMode: 'forwards' }}>
                <ShieldCheck className="w-4 h-4 text-[#9055EE]" />
                <T page={P} k="hero.cards.compliance" d="Conform wet Loontransparantie" as="span" className="text-sm font-semibold text-gray-700" />
              </div>
              <div className="absolute -right-6 bottom-24 bg-white rounded-full shadow-xl ring-[8px] ring-white/80 px-4 py-2.5 transform -rotate-2 hover:rotate-0 transition-transform duration-300 hidden lg:flex items-center gap-2 opacity-0 animate-fade-float-in" style={{ animationDelay: '1400ms', animationFillMode: 'forwards' }}>
                <CheckCircle2 className="w-4 h-4 text-green-500" />
                <T page={P} k="hero.cards.noExcel" d="Salarisronde zonder Excel" as="span" className="text-sm font-semibold text-gray-700" />
              </div>
              <div className="absolute -left-6 bottom-10 bg-white rounded-full shadow-xl ring-[8px] ring-white/80 px-4 py-2.5 transform -rotate-2 hover:rotate-0 transition-transform duration-300 hidden lg:flex items-center gap-2 opacity-0 animate-fade-float-in" style={{ animationDelay: '1700ms', animationFillMode: 'forwards' }}>
                <Scale className="w-4 h-4 text-[#FB9A24]" />
                <T page={P} k="hero.cards.explainable" d="Uitlegbaar per medewerker" as="span" className="text-sm font-semibold text-gray-700" />
              </div>
            </div>
          </div>
          <ClientLogos />
        </PageContainer>
      </section>


      {/* Intro banner */}
      <section className="px-6 py-20" style={{ backgroundColor: '#F3EDFD' }}>
        <PageContainer>
          <div className="text-center">
            <T page={P} k="intro.eyebrow" d="Learned Compensation" as="p" className="text-xs font-semibold uppercase tracking-wider text-[#9055EE] mb-2" />
            <h2 className="feature-section-heading text-foreground mb-4">
              <T page={P} k="intro.title" d="Beloon eerlijk, voorkom boetes en eindeloze discussies" />
            </h2>
            <THtml
              page={P}
              k="intro.body"
              d="Learned Compensation verbindt je functiehuis, salarisronde en onderbouwing tot één transparant proces, inclusief koppeling met je HRIS. Zo neem je elke beloningsbeslissing sneller, onderbouwd met data, en uitlegbaar en transparant naar iedereen."
              as="p"
              className="text-lead text-muted-foreground max-w-4xl mx-auto"
            />
            <div className="mx-auto mt-10 w-full max-w-4xl">
              <div className="relative aspect-video overflow-hidden rounded-2xl shadow-lg bg-foreground/5">
                <iframe
                  src={VIDEO_SRC_BY_LANG[lang] ?? VIDEO_SRC_BY_LANG.nl}
                  className="absolute inset-0 h-full w-full"
                  frameBorder="0"
                  allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
                  referrerPolicy="strict-origin-when-cross-origin"
                  allowFullScreen
                  title="Learned Framework | Work Design, Performance Management & Compensation"
                />
              </div>
            </div>
            <div className="mt-8">
              <Button
                size="lg"
                onClick={() => document.getElementById('compensation-onderdelen')?.scrollIntoView({ behavior: 'smooth' })}
              >
                <T page={P} k="intro.ctaParts" d="Ontdek de onderdelen" />
              </Button>
            </div>
          </div>
        </PageContainer>
      </section>
      {/* Challenge Block */}
      <section
        className="overflow-x-hidden px-4 py-20 sm:px-6"
        style={{ background: "linear-gradient(135deg, #2A2334 0%, #4A3660 50%, #2A2334 100%)" }}
      >
        <div className="mx-auto w-full max-w-5xl">
          <div className="grid items-center gap-12 lg:grid-cols-2 lg:gap-16">
            <div className="mx-auto flex w-full max-w-sm flex-col items-center gap-3 sm:max-w-none sm:flex-row sm:flex-wrap sm:justify-center sm:gap-4">
              {[
                { icon: Clock, k: "challenges.pain1", d: "Tijdrovende, foutgevoelige salarisrondes" },
                { icon: TrendingDown, k: "challenges.pain2", d: "Onverklaarbare loonverschillen" },
                { icon: Users, k: "challenges.pain3", d: "Wantrouwen en verloop van talent" },
                { icon: AlertTriangle, k: "challenges.pain4", d: "Niet compliant met de wet Loontransparantie" },
              ].map((point, i) => {
                const Icon = point.icon;
                return (
                  <div
                    key={i}
                    className="flex w-full items-center justify-center gap-3 rounded-full bg-white/10 px-4 py-3 backdrop-blur-sm sm:w-auto sm:px-5"
                  >
                    <Icon className="w-4 h-4 text-white/60 shrink-0" />
                    <T page={P} k={point.k} d={point.d} as="span" className="text-center text-sm leading-snug text-white/80" />
                  </div>
                );
              })}
            </div>

            <div>
              <T page={P} k="challenges.eyebrow" d="De uitdaging" as="p" className="text-xs font-semibold uppercase tracking-wider text-white/60 mb-2" />
              <h2 className="feature-section-heading text-white mb-6">
                <T page={P} k="challenges.title" d="Geen eerlijke beloning zonder onderbouwd beleid" />
              </h2>
              <THtml
                page={P}
                k="challenges.body"
                d="De meeste organisaties belonen nog op basis van onderhandeling en onderbuikgevoel, niet op basis van data. Voor HR betekent dat een tijdrovende, foutgevoelige exercitie zonder sluitend antwoord. Voor de organisatie loopt het risico verder: vertrouwen brokkelt af, talent vertrekt, en vanaf 2027 riskeer je een boete als je niet voldoet aan de wet Loontransparantie."
                as="p"
                className="text-sm text-white/80 leading-relaxed"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="pt-[60px] pb-10 px-6">
        <PageContainer>
          <div className="space-y-16">
            {features.map((feature, index) => {
              const isReversed = index % 2 === 1;
              return (
                <div
                  key={index}
                  id={index === 0 ? 'compensation-onderdelen' : undefined}
                  className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center animate-fade-in"
                  style={{ animationDelay: `${index * 100}ms` }}>

                  <div className={`${isReversed ? 'lg:order-2' : 'lg:order-1'}`}>
                    <T page={P} k={feature.eyebrowKey} d={feature.eyebrowDefault} as="p" className="text-xs font-semibold uppercase tracking-wider text-[#9055EE] mb-2" />
                    <h2 className="feature-section-heading text-foreground">
                      <T page={P} k={feature.titleKey} d={feature.titleDefault} />
                    </h2>
                    <THtml page={P} k={feature.bodyKey} d={feature.bodyDefault} as="p" className="text-lead text-muted-foreground" />

                    <ul className="mt-6 space-y-3">
                      {feature.benefits.map((benefit) =>
                      <li key={benefit.k} className="flex items-start gap-3">
                          <CheckCircle2 className="w-5 h-5 text-[#9055EE] shrink-0 mt-0.5" />
                          <T page={P} k={benefit.k} d={benefit.d} as="span" className="text-sm text-muted-foreground" />
                        </li>
                      )}
                    </ul>

                    {feature.cta &&
                    <Button
                      asChild
                      className="mt-6 rounded-full bg-[#9055EE] hover:bg-[#753DCE] text-white">
                        <Link to={feature.cta.link}>
                          <T page={P} k={feature.cta.labelKey} d={feature.cta.labelDefault} />
                          <ArrowRight className="ml-2 h-4 w-4" />
                        </Link>
                      </Button>
                    }
                    {feature.demoCta &&
                    <Button
                      className="mt-6 rounded-full bg-[#9055EE] hover:bg-[#753DCE] text-white"
                      onClick={() => document.getElementById('schedule-demo')?.scrollIntoView({ behavior: 'smooth' })}>

                        <T page={P} k="features.demoCta" d="Plan een demo" />
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    }
                  </div>
                  <div className={`relative ${isReversed ? 'lg:order-1' : 'lg:order-2'}`}>
                    {('imgSrc' in feature ? (feature as { imgSrc?: string }).imgSrc : undefined) ?
                    <EditableImage
                      page={P}
                      section="features"
                      imageKey={feature.imageKey}
                      src={(feature as { imgSrc?: string }).imgSrc || ''}
                      alt={('imgAlt' in feature ? (feature as { imgAlt?: string }).imgAlt : undefined) || ''}
                      loading="lazy"
                      className="w-full h-auto rounded-2xl"
                    /> :
                    <div
                      className="rounded-2xl p-4 md:p-6 flex items-center justify-center relative overflow-hidden min-h-[400px] max-h-[450px]"
                      style={{
                        background: `radial-gradient(circle at center, white 0%, ${feature.color} 70%)`,
                        border: '10px solid rgba(255, 255, 255, 0.7)'
                      }}>

                      {/* TODO: beeld toevoegen via de CMS (Visuals: page platform-compensation, section features) */}
                      <EditableImage
                        page={P}
                        section="features"
                        imageKey={feature.imageKey}
                        src=""
                        alt=""
                        loading="lazy"
                        className="w-full max-w-full max-h-[400px] object-contain relative z-10 mx-auto"
                      />
                    </div>}
                    {('badge' in feature ? (feature as { badge?: { k: string; d: string } }).badge : undefined) &&
                    <div className="absolute -right-4 top-5 bg-[#4B2A8A] rounded-full shadow-xl ring-[8px] ring-white/80 px-4 py-2.5 hidden lg:flex items-center gap-2 z-20">
                      <CheckCircle2 className="w-4 h-4 text-green-400" />
                      <T page={P} k={(feature as { badge: { k: string; d: string } }).badge.k} d={(feature as { badge: { k: string; d: string } }).badge.d} as="span" className="text-sm font-semibold text-white" />
                    </div>}
                  </div>
                </div>);

            })}
          </div>
        </PageContainer>
      </section>

      <section className="px-6 pt-0 pb-12">
        <PageContainer>
          <div
            className="rounded-2xl px-8 py-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between"
            style={{ backgroundColor: '#F3EDFD' }}>

            <p className="text-base text-foreground">
              <T page={P} k="nextModule.text" d="Compensation bouwt op het functiehuis en salarishuis uit Work Design." />
            </p>
            <Link
              to={getLocalizedRoutePath("workDesign", lang)}
              className="inline-flex items-center gap-2 shrink-0 text-base font-medium text-[#9055EE] hover:text-[#753DCE] transition-colors">

              <T page={P} k="nextModule.link" d="Ontdek Work Design" />
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </PageContainer>
      </section>

      <PageResources page="platform-compensation" cards={[
      { href: `/${lang}/blog/${lang === "en" ? "how-to-build-a-salary-structure" : lang === "de" ? "gehaltsstruktur-erstellen" : "salarishuis-opstellen"}/`, image: "/images/blog/salarishuis-opstellen-hero.webp", alt: "Salarishuis opstellen", type: "blog", titleKey: "resources.card4.title", titleDefault: "Salarishuis opstellen: stappenplan, voorbeeld en de wet Loontransparantie" },
      { href: `/${lang}/webinar/wet-loontransparantie`, image: "/images/resources/loontransparantie-webinar.webp", alt: "Webinar wet Loontransparantie", type: "webinar", titleKey: "resources.card1.title", titleDefault: "On-demand webinar: klaar voor de wet Loontransparantie" },
      { href: `/${lang}/webinar/functiehuis-performance-beloning`, image: "/images/resources/functiehuis-performance-beloning.png", alt: "Webinar functiehuis, performance en beloning", type: "webinar", titleKey: "resources.card3.title", titleDefault: "On-demand webinar: verbind functiehuis, performance en beloning in één HR-aanpak" }]} />


      <PartnerForChange />

      <ScheduleDemo titleKey="titleCompensation" titleDefault="Klaar om beloning uitlegbaar te maken?" />
    </>);

};

export default PlatformCompensation;
